import { useMemo, useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Megaphone, Handshake, Anchor as AnchorIcon, DollarSign } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

type Role = "advocate" | "partner" | "anchor" | "affiliate";

const ROLE_OPTIONS: {
  key: Role;
  label: string;
  blurb: string;
  icon: typeof Megaphone;
}[] = [
  {
    key: "advocate",
    label: "Advocate",
    blurb: "Amplifier or community connector. Referral link + share kit.",
    icon: Megaphone,
  },
  {
    key: "partner",
    label: "Partner",
    blurb: "Vetted service provider — legal, capital, build, distribution, ops. Public profile + lead routing.",
    icon: Handshake,
  },
  {
    key: "anchor",
    label: "Anchor",
    blurb: "Strategic anchor with a named commitment. Co-branded programs + featured placement.",
    icon: AnchorIcon,
  },
  {
    key: "affiliate",
    label: "Affiliate",
    blurb: "Earn revenue share on paid subscribers you refer. Dashboard + payout tracking.",
    icon: DollarSign,
  },
];

const SERVICES = [
  "Legal",
  "Capital / Funding",
  "Build / Tech",
  "Distribution / Marketing",
  "Mentorship",
  "Operations",
  "Sourcing / Production",
  "Other",
];

const rolesFromParams = (raw: string | null): Role[] => {
  if (!raw) return [];
  const allowed: Role[] = ["advocate", "partner", "anchor", "affiliate"];
  return raw
    .split(",")
    .map((r) => r.trim().toLowerCase())
    .filter((r): r is Role => (allowed as string[]).includes(r));
};

// Highest of advocate < partner < anchor among the roles; falls back to advocate.
const deriveTier = (roles: Role[]): "advocate" | "partner" | "anchor" => {
  if (roles.includes("anchor")) return "anchor";
  if (roles.includes("partner")) return "partner";
  return "advocate";
};

export default function PartnerApply() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const initialRoles = useMemo<Role[]>(() => {
    const fromRoles = rolesFromParams(params.get("roles") || params.get("role"));
    if (fromRoles.length) return fromRoles;
    const t = params.get("tier");
    if (t === "advocate" || t === "partner" || t === "anchor") return [t];
    return ["partner"];
  }, [params]);

  const [roles, setRoles] = useState<Role[]>(initialRoles);
  const [orgName, setOrgName] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState(user?.email ?? "");
  const [website, setWebsite] = useState("");
  const [services, setServices] = useState<string[]>([]);
  const [counties, setCounties] = useState("");
  const [why, setWhy] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user?.email && !contactEmail) setContactEmail(user.email);
  }, [user, contactEmail]);

  const toggleRole = (r: Role) =>
    setRoles((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));
  const toggleService = (s: string) =>
    setServices((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const showServices = roles.some((r) => r === "partner" || r === "anchor" || r === "affiliate");
  const showCounties = roles.some((r) => r === "partner" || r === "anchor");

  const valid = useMemo(
    () =>
      roles.length > 0 &&
      orgName.trim().length >= 2 &&
      contactName.trim().length >= 2 &&
      /.+@.+\..+/.test(contactEmail),
    [roles, orgName, contactName, contactEmail],
  );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) {
      toast.error("Pick at least one layer and fill in org name, contact name, and a valid email.");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("partner_applications").insert({
      applicant_user_id: user?.id ?? null,
      org_name: orgName.trim(),
      contact_name: contactName.trim(),
      contact_email: contactEmail.trim(),
      website: website.trim() || null,
      tier_requested: deriveTier(roles),
      roles_requested: roles,
      services,
      counties: counties
        .split(",")
        .map((c) => c.trim())
        .filter(Boolean),
      why: why.trim() || null,
    });
    setSubmitting(false);
    if (error) {
      toast.error("Could not submit application", { description: error.message });
      return;
    }
    toast.success("Application received. We'll be in touch.");
    navigate("/partners");
  };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="px-4 sm:px-6 py-12 max-w-2xl mx-auto">
        <Link to="/partners" className="text-sm text-muted-foreground hover:text-foreground">← Back to program</Link>
        <h1 className="font-display text-3xl font-bold mt-3 mb-2">Join the network</h1>
        <p className="text-muted-foreground mb-8">
          One application, any layers. Pick every way you want to work with us — we review weekly and respond within five business days.
        </p>
        <Card className="p-6">
          <form onSubmit={onSubmit} className="space-y-6">
            <div>
              <Label className="text-base">Which layers are you applying for? *</Label>
              <p className="text-xs text-muted-foreground mt-1">Pick one or more. You can be an advocate, a partner, and an affiliate at once.</p>
              <div className="mt-3 grid sm:grid-cols-2 gap-3">
                {ROLE_OPTIONS.map(({ key, label, blurb, icon: Icon }) => {
                  const checked = roles.includes(key);
                  return (
                    <label
                      key={key}
                      className={`flex gap-3 rounded-lg border p-3 cursor-pointer transition ${
                        checked ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
                      }`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleRole(key)}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 font-semibold text-sm">
                          <Icon className="h-3.5 w-3.5 text-primary" />
                          {label}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 leading-snug">{blurb}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="org">Organization name *</Label>
                <Input id="org" value={orgName} onChange={(e) => setOrgName(e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="website">Website</Label>
                <Input id="website" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="cname">Contact name *</Label>
                <Input id="cname" value={contactName} onChange={(e) => setContactName(e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="cemail">Contact email *</Label>
                <Input id="cemail" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className="mt-1.5" />
              </div>
            </div>

            {showServices && (
              <div>
                <Label>Services you provide</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {SERVICES.map((s) => {
                    const active = services.includes(s);
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => toggleService(s)}
                        className={`text-xs px-3 py-1.5 rounded-full border transition ${
                          active
                            ? "bg-primary text-primary-foreground border-primary"
                            : "border-border text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {showCounties && (
              <div>
                <Label htmlFor="counties">Counties you serve (comma-separated)</Label>
                <Input
                  id="counties"
                  value={counties}
                  onChange={(e) => setCounties(e.target.value)}
                  placeholder="Mobile, Baldwin, Jefferson"
                  className="mt-1.5"
                />
              </div>
            )}

            <div>
              <Label htmlFor="why">Why do you want to join? What should we know?</Label>
              <Textarea
                id="why"
                value={why}
                onChange={(e) => setWhy(e.target.value)}
                placeholder="What you bring, who you serve, anything we should know."
                className="mt-1.5 min-h-[120px]"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={submitting}>
                {submitting ? "Submitting…" : "Submit application"}
              </Button>
              <Button type="button" variant="outline" asChild>
                <Link to="/partners">Cancel</Link>
              </Button>
            </div>
          </form>
        </Card>
      </main>
      <SiteFooter />
    </div>
  );
}